import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, eur, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  LineChart, ShieldCheck, Lock, Download, FileSpreadsheet, ChevronRight,
  ArrowLeft, LogOut, Building2, Loader2, Mail, KeyRound, Scale, Boxes, Save,
} from "lucide-react";

const GRANT_KEY = "res_grant";
const grantHeader = () => ({ "X-Resultaten-Token": sessionStorage.getItem(GRANT_KEY) || "" });

const iso = (d) => d.toISOString().slice(0, 10);
const presets = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = Math.floor(m / 3);
  return {
    maand: { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) },
    kwartaal: { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)) },
    jaar: { from: `${y}-01-01`, to: `${y}-12-31` },
  };
};

function Gate({ onGranted }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    try {
      const r = await api.post("/resultaten/auth/open", { code });
      sessionStorage.setItem(GRANT_KEY, r.data.grant);
      onGranted();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-sm mx-auto mt-16" data-testid="resultaten-gate">
      <div className="flex flex-col items-center text-center mb-6">
        <div className="h-14 w-14 rounded-2xl bg-emerald-600 flex items-center justify-center mb-4">
          <ShieldCheck className="h-7 w-7 text-white" />
        </div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Beveiligde toegang</h1>
        <p className="text-sm text-muted-foreground mt-1">Voer de toegangscode in om de resultatenrekening te openen.</p>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="res-code" className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> Toegangscode</Label>
            <Input id="res-code" type="password" inputMode="numeric" autoFocus data-testid="resultaten-code"
              className="tracking-[0.3em] text-center font-mono text-lg"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 12))}
              onKeyDown={(e) => e.key === "Enter" && code.length >= 4 && open()} placeholder="••••••••" />
          </div>
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={code.length < 4 || busy}
            data-testid="resultaten-verify-btn" onClick={open}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Openen
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, sub, accent, testid }) {
  return (
    <div className={`rounded-xl border p-4 ${accent}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold font-heading mt-1 tabular-nums">{eur(value)}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Saldibalans({ range, onLocked }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState(null);
  const [stockDate, setStockDate] = useState(range.to);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/resultaten/saldibalans", {
        params: { from_date: range.from, to_date: range.to }, headers: grantHeader(),
      });
      setData(r.data);
      setCats(r.data.stock.categories.map((c) => ({ ...c })));
      setStockDate(r.data.stock.date || range.to);
    } catch (e) {
      if (e.response?.status === 403) { sessionStorage.removeItem(GRANT_KEY); onLocked(); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [range.from, range.to, onLocked]);
  useEffect(() => { load(); }, [load]);

  const setCat = (key, v) => setCats((cs) => cs.map((c) => (c.key === key ? { ...c, value: v } : c)));
  const stockTotal = (cats || []).reduce((a, c) => a + (parseFloat(c.value) || 0), 0);

  const saveStock = async () => {
    setSaving(true);
    try {
      const payload = { date: stockDate || range.to, categories: {} };
      (cats || []).forEach((c) => { payload.categories[c.key] = parseFloat(c.value) || 0; });
      await api.post("/resultaten/stock", payload, { headers: grantHeader() });
      toast.success("Eindvoorraad opgeslagen — balans bijgewerkt");
      load();
    } catch (e) {
      if (e.response?.status === 403) { sessionStorage.removeItem(GRANT_KEY); onLocked(); return; }
      toast.error("Opslaan mislukt");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-center text-muted-foreground py-16">Laden…</div>;
  if (!data) return null;
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="saldibalans-view">
      {/* Voorraad per BTW-soort */}
      <Card data-testid="voorraad-card">
        <CardContent className="pt-5 space-y-4">
          <div className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-emerald-600" />
            <h2 className="font-heading text-lg font-semibold">Eindvoorraad per BTW-soort</h2>
          </div>
          <p className="text-sm text-muted-foreground">Geef de eindvoorraad in aankoopprijs <b>excl. btw</b> in. Deze wordt verwerkt als voorraad handelsgoederen (34) met de voorraadwijziging (609) in de balans.</p>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Voorraaddatum</Label>
              <Input type="date" className="h-9 w-[150px]" data-testid="stock-date" value={stockDate} onChange={(e) => setStockDate(e.target.value)} />
            </div>
            {(cats || []).map((c) => (
              <div key={c.key} className="space-y-1.5">
                <Label className="text-xs">{c.label}</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                  <Input type="number" step="0.01" className="h-9 w-[150px] pl-6 tabular-nums" data-testid={`stock-${c.key}`}
                    value={c.value} onChange={(e) => setCat(c.key, e.target.value)} />
                </div>
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Totaal</Label>
              <div className="h-9 flex items-center font-semibold tabular-nums" data-testid="stock-total">{eur(stockTotal)}</div>
            </div>
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} data-testid="stock-save" onClick={saveStock}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Opslaan
            </Button>
          </div>
          {Math.abs(data.stock.delta) > 0.005 && (
            <p className="text-xs text-muted-foreground">Beginvoorraad (uit grootboek): {eur(data.stock.opening)} · voorraadwijziging: <b className={data.stock.delta >= 0 ? "text-emerald-700" : "text-red-700"}>{eur(data.stock.delta)}</b></p>
          )}
        </CardContent>
      </Card>

      {/* Klasse-subtotalen + resultaat */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard testid="balans-resultaat" label="Resultaat periode" value={data.resultaat}
          accent={data.resultaat >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"} />
        <KpiCard testid="balans-voorraad" label="Voorraad (34)" value={data.stock.total} accent="bg-card" />
        <KpiCard testid="balans-debet" label="Totaal debet" value={t.debet} accent="bg-card" />
        <KpiCard testid="balans-credit" label="Totaal credit" value={t.credit} accent="bg-card" />
      </div>

      {/* Proef- en saldibalans */}
      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-[1fr_repeat(4,110px)] gap-2 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/50 border-b">
            <span>Rekening</span>
            <span className="text-right">Debet</span><span className="text-right">Credit</span>
            <span className="text-right">Saldo debet</span><span className="text-right">Saldo credit</span>
          </div>
          {data.rows.map((r) => (
            <div key={r.account} data-testid={`balans-row-${r.account}`}
              className={`grid grid-cols-[1fr_repeat(4,110px)] gap-2 px-4 py-1.5 items-center text-sm border-b last:border-b-0 ${r.stock_adjust ? "bg-amber-50/60" : ""}`}>
              <span className="truncate"><span className="font-mono text-xs text-muted-foreground">{r.account}</span> · {r.name}{r.stock_adjust ? " (voorraad)" : ""}</span>
              <span className="text-right tabular-nums text-muted-foreground">{r.debet ? eur(r.debet) : ""}</span>
              <span className="text-right tabular-nums text-muted-foreground">{r.credit ? eur(r.credit) : ""}</span>
              <span className="text-right tabular-nums font-medium">{r.saldo_debet ? eur(r.saldo_debet) : ""}</span>
              <span className="text-right tabular-nums font-medium">{r.saldo_credit ? eur(r.saldo_credit) : ""}</span>
            </div>
          ))}
          <div className="grid grid-cols-[1fr_repeat(4,110px)] gap-2 px-4 py-2.5 items-center text-sm bg-indigo-50/60 font-semibold border-t" data-testid="balans-totals">
            <span>Totaal</span>
            <span className="text-right tabular-nums">{eur(t.debet)}</span>
            <span className="text-right tabular-nums">{eur(t.credit)}</span>
            <span className="text-right tabular-nums">{eur(t.saldo_debet)}</span>
            <span className="text-right tabular-nums">{eur(t.saldo_credit)}</span>
          </div>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">{data.period_label} · proef- en saldibalans op basis van alle bevestigde grootboekboekingen, met tussentijdse voorraadcorrectie.</p>
    </div>
  );
}

function Report({ onLocked }) {
  const p = presets();
  const [range, setRange] = useState({ ...p.jaar });
  const [activePreset, setActivePreset] = useState("jaar");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [downloading, setDownloading] = useState("");
  const [view, setView] = useState("pl");

  const load = useCallback(async () => {
    if (view !== "pl") return;
    setLoading(true);
    try {
      const r = await api.get("/resultaten/pl", {
        params: { from_date: range.from, to_date: range.to }, headers: grantHeader(),
      });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 403) { sessionStorage.removeItem(GRANT_KEY); onLocked(); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [range.from, range.to, onLocked, view]);

  useEffect(() => { load(); }, [load]);

  const setPreset = (key) => { setActivePreset(key); setRange({ ...p[key] }); };

  const download = async (format) => {
    setDownloading(format);
    try {
      const isBalans = view === "balans";
      const r = await api.get(isBalans ? "/resultaten/saldibalans" : "/resultaten/export", {
        params: { from_date: range.from, to_date: range.to, format }, headers: grantHeader(), responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const base = isBalans ? "proef_en_saldibalans" : "resultatenrekening";
      a.download = `${base}_${range.from}_${range.to}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 403) { sessionStorage.removeItem(GRANT_KEY); onLocked(); return; }
      toast.error("Download mislukt");
    } finally { setDownloading(""); }
  };

  const s = data?.summary;
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="resultaten-report">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            {view === "pl" ? <><LineChart className="h-6 w-6 text-emerald-600" /> Resultatenrekening</>
              : <><Scale className="h-6 w-6 text-emerald-600" /> Proef- en saldibalans</>}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {view === "pl" ? "Winst- en verliesrekening op basis van de bevestigde grootboekboekingen."
              : "Tussentijdse proef- en saldibalans met voorraad per BTW-soort."}
          </p>
        </div>
        {(view === "pl" || view === "balans") && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="download-pdf" disabled={downloading} onClick={() => download("pdf")}>
              {downloading === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />} PDF
            </Button>
            <Button variant="outline" size="sm" data-testid="download-excel" disabled={downloading} onClick={() => download("xlsx")}>
              {downloading === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />} Excel
            </Button>
          </div>
        )}
      </div>

      <div className="inline-flex rounded-lg border bg-card p-0.5" data-testid="resultaten-tabs">
        {[["pl", "Resultatenrekening"], ["balans", "Proef- & saldibalans"]].map(([k, lbl]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setView(k)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === k ? "bg-emerald-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>
            {lbl}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border bg-card p-0.5" data-testid="period-presets">
          {[["maand", "Deze maand"], ["kwartaal", "Dit kwartaal"], ["jaar", "Dit jaar"]].map(([k, lbl]) => (
            <button key={k} data-testid={`preset-${k}`} onClick={() => setPreset(k)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activePreset === k ? "bg-emerald-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Input type="date" className="h-9 w-[150px]" data-testid="from-date" value={range.from}
            onChange={(e) => { setActivePreset(""); setRange((r) => ({ ...r, from: e.target.value })); }} />
          <span className="text-muted-foreground">→</span>
          <Input type="date" className="h-9 w-[150px]" data-testid="to-date" value={range.to}
            onChange={(e) => { setActivePreset(""); setRange((r) => ({ ...r, to: e.target.value })); }} />
        </div>
      </div>

      {view === "balans" ? (
        <Saldibalans range={range} onLocked={onLocked} />
      ) : loading ? (
        <div className="text-center text-muted-foreground py-16">Laden…</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard testid="kpi-omzet" label="Netto omzet" value={s.omzet} accent="bg-card" />
            <KpiCard testid="kpi-brutowinst" label="Brutowinst" value={s.brutowinst} sub={`Marge ${String(s.marge_pct).replace(".", ",")}%`} accent="bg-card" />
            <KpiCard testid="kpi-bedrijfsresultaat" label="Bedrijfsresultaat" value={s.bedrijfsresultaat} accent="bg-card" />
            <KpiCard testid="kpi-resultaat" label="Resultaat na belasting" value={s.resultaat_na_belasting}
              accent={s.resultaat_na_belasting >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"} />
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="grid grid-cols-[1fr_140px_70px] gap-2 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/50 border-b">
                <span>Omschrijving</span><span className="text-right">Bedrag</span><span className="text-right">% omzet</span>
              </div>
              {data.rows.map((row) => {
                const canOpen = (row.accounts || []).length > 0;
                const isOpen = !!open[row.key];
                const neg = row.amount < 0;
                return (
                  <div key={row.key} className="border-b last:border-b-0" data-testid={`pl-row-${row.key}`}>
                    <button type="button" disabled={!canOpen}
                      onClick={() => canOpen && setOpen((o) => ({ ...o, [row.key]: !o[row.key] }))}
                      className={`w-full grid grid-cols-[1fr_140px_70px] gap-2 px-4 py-2.5 items-center text-left ${row.result ? "bg-indigo-50/60" : ""} ${canOpen ? "hover:bg-muted/40 cursor-pointer" : "cursor-default"}`}>
                      <span className={`flex items-center gap-1.5 ${row.result || row.bold ? "font-semibold" : ""} ${row.result ? "text-indigo-900" : ""}`}>
                        {canOpen && <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`} />}
                        {!canOpen && <span className="w-3.5 shrink-0" />}
                        {row.label}
                      </span>
                      <span className={`text-right tabular-nums ${row.result || row.bold ? "font-semibold" : ""} ${row.result ? (neg ? "text-red-700" : "text-emerald-700") : ""}`}>{eur(row.amount)}</span>
                      <span className="text-right tabular-nums text-xs text-muted-foreground">{String(row.pct).replace(".", ",")}%</span>
                    </button>
                    {canOpen && isOpen && (
                      <div className="bg-muted/20" data-testid={`pl-detail-${row.key}`}>
                        {row.accounts.map((a) => (
                          <div key={a.account} className="grid grid-cols-[1fr_140px_70px] gap-2 px-4 py-1.5 items-center text-xs border-t border-dashed">
                            <span className="pl-6 text-muted-foreground"><span className="font-mono">{a.account}</span> · {a.name}</span>
                            <span className="text-right tabular-nums text-muted-foreground">{eur(a.amount)}</span>
                            <span></span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground">{data.period_label} · enkel bevestigde grootboekboekingen. Bedragen in euro; % = aandeel van de netto omzet.</p>
        </>
      )}
    </div>
  );
}

export default function Resultaten() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [granted, setGranted] = useState(!!sessionStorage.getItem(GRANT_KEY));

  return (
    <div className="min-h-screen bg-background" data-testid="resultaten-module">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" data-testid="res-back-hub" onClick={() => navigate("/hub")}>
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Modules
            </Button>
            <div className="hidden sm:flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-md bg-emerald-600 flex items-center justify-center text-white">
                <Building2 className="h-4 w-4" />
              </div>
              <div className="leading-tight">
                <div className="font-heading font-semibold text-sm">Singh Express Shop BV</div>
                <div className="text-xs text-muted-foreground">Resultaten</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="sm" data-testid="res-logout" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Afmelden
            </Button>
          </div>
        </div>
      </header>
      <main className="px-6 py-10">
        {granted ? <Report onLocked={() => setGranted(false)} /> : <Gate onGranted={() => setGranted(true)} />}
      </main>
    </div>
  );
}
